import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiArrowRight, FiCpu, FiSun, FiCheckSquare, FiMessageSquare,
  FiTarget, FiExternalLink, FiLoader,
} from 'react-icons/fi';
import { useSector } from '../../contexts/SectorContext';
import {
  isMendsVisionEnabled,
  mindChat,
  createApproval,
  approveItem,
  MindChatAction,
  CentralQuestionAnswer,
} from '../services/MendsVisionService';
import { Markdown } from './Markdown';
import {
  CentralQuestionContext,
  CENTRAL_SUGGESTED_QUESTIONS,
  centralStatusLabel,
  subscribeCentralQuestion,
} from '../lib/centralQuestion';
import { MaiConfirmDialog } from './MaiConfirmDialog';
import { readActiveContact } from '../lib/activeContact';
import { apiErrorMessage } from '../../utils/apiErrorMessage';
import { describeCreateOutcome } from '../lib/createApprovalOutcome';
import '../styles/mendsAi.css';

export type CmdItem = {
  id: string;
  title: string;
  subtitle: string;
  keywords: string[];
  run: () => void;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

type InlineAction = MindChatAction & {
  id: string;
  status?: 'idle' | 'busy' | 'done' | 'error';
  message?: string;
};

function normalize(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function isDestructive(a: MindChatAction): boolean {
  const blob = `${a.actionKind || ''} ${a.path || ''} ${a.title || ''}`.toLowerCase();
  return /delete|remove|excluir|apagar|desligar|destru/.test(blob);
}

function isHandoff(a: MindChatAction): boolean {
  const blob = `${a.actionKind || ''} ${a.path || ''}`.toLowerCase();
  return blob.includes('transfer') || blob.includes('handoff');
}

function needsConfirm(a: MindChatAction): boolean {
  return a.requiresConfirm || a.kind === 'mutation' || isDestructive(a);
}

/**
 * Barra da IA (⌘K): consulta + comando + execução inline.
 * Acontecimentos do mundo real ficam no Registrar (separado).
 */
export const CommandPalette: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { selectedSectorId } = useSector();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState<string | null>(null);
  const [actions, setActions] = useState<InlineAction[]>([]);
  const [confirm, setConfirm] = useState<InlineAction | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingHandoff = useRef<{ sectorId: number; contactId: number; name: string } | null>(null);
  const awaitingDestination = useRef(false);
  /** Aberta pelo "Perguntar à MENDS" da Central: herda conexões e período da tela. */
  const [centralCtx, setCentralCtx] = useState<CentralQuestionContext | null>(null);
  const [central, setCentral] = useState<CentralQuestionAnswer | null>(null);
  /** Últimas perguntas e respostas desta abertura: "e os detalhes?" sabe do que se trata. */
  const history = useRef<{ role: string; content: string }[]>([]);

  useEffect(() => subscribeCentralQuestion((detail) => setCentralCtx(detail)), []);
  useEffect(() => {
    if (open) return;
    setCentralCtx(null);
    history.current = [];
  }, [open]);

  const goMind = useCallback((prompt?: string, voice?: boolean) => {
    const params = new URLSearchParams();
    if (prompt) params.set('q', prompt);
    if (voice) params.set('voice', '1');
    const qs = params.toString();
    navigate(qs ? `/mente?${qs}` : '/mente');
    onClose();
    window.setTimeout(() => {
      if (voice) window.dispatchEvent(new CustomEvent('mends-mind-voice'));
      else if (prompt) window.dispatchEvent(new CustomEvent('mends-mind-prompt', { detail: { prompt } }));
      else window.dispatchEvent(new CustomEvent('mends-mind-focus'));
    }, 80);
  }, [navigate, onClose]);

  const resetInline = () => {
    pendingHandoff.current = null;
    awaitingDestination.current = false;
    setReply(null);
    setActions([]);
    setCentral(null);
    setBusy(false);
    setConfirm(null);
  };

  const runNl = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q) return;
    if (!selectedSectorId) {
      setReply('Selecione uma conexão no menu para eu consultar e executar.');
      return;
    }
    const handoff = awaitingDestination.current ? pendingHandoff.current : null;
    if (handoff) {
      awaitingDestination.current = false;
      pendingHandoff.current = null;
    }
    const active = readActiveContact();
    const sectorId = Number(selectedSectorId);
    const contactId = handoff?.contactId
      ?? (active && active.sectorId === sectorId ? active.contactId : undefined);
    const message = handoff
      ? `Transfira o contato ${handoff.name} (contatoId=${handoff.contactId}) para ${q} com o contexto da conversa.`
      : q;
    setBusy(true);
    setReply(null);
    setActions([]);
    setCentral(null);
    try {
      const res = await mindChat({
        sectorId,
        message,
        contactId,
        history: history.current.slice(-4),
        centralSectorIds: centralCtx?.sectorIds,
        centralPeriod: centralCtx?.period,
        source: centralCtx ? 'central' : 'palette',
      });
      const text = (res.reply || '').trim() || 'Pronto.';
      history.current = [...history.current, { role: 'user', content: q }, { role: 'assistant', content: text }].slice(-6);
      setCentral(res.central ?? null);
      setReply(text);
      setActions((res.actions || []).map((a, i) => ({
        ...a,
        id: `a-${i}-${a.path || a.title}`,
        status: 'idle',
      })));
    } catch (e: unknown) {
      setReply(apiErrorMessage(e, 'Não consegui processar agora.'));
    } finally {
      setBusy(false);
    }
  }, [selectedSectorId, centralCtx]);

  const patchAction = (id: string, patch: Partial<InlineAction>) => {
    setActions((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };

  const executeAction = async (card: InlineAction, force = false) => {
    if (!selectedSectorId) return;
    if (!force && (isDestructive(card) || isHandoff(card))) {
      setConfirm(card);
      return;
    }
    const path = card.path || '';
    if (path.startsWith('/') && !needsConfirm(card) && !path.startsWith('mutation:')) {
      navigate(path);
      onClose();
      return;
    }

    patchAction(card.id, { status: 'busy', message: 'Executando…' });
    try {
      const created = await createApproval({
        sectorId: Number(selectedSectorId),
        contactId: card.contactId ?? null,
        contactName: card.kind === 'contact' ? card.title : null,
        title: card.approvalTitle || card.title,
        body: card.approvalBody || card.subtitle || card.title,
        suggestedAction: card.suggestedAction || 'Revisar e confirmar',
        actionKind: card.actionKind || undefined,
        actionPayload: card.actionPayload || undefined,
      });
      const outcome = describeCreateOutcome(created);
      if (outcome.kind === 'not_created') {
        patchAction(card.id, { status: 'error', message: outcome.message });
        return;
      }
      if (outcome.kind === 'executed') {
        patchAction(card.id, { status: 'done', message: outcome.message });
        return;
      }
      // Sensível: só enfileira. Operacional autorizado: cria e executa sem edit redundante.
      if (needsConfirm(card) || isDestructive(card)) {
        patchAction(card.id, {
          status: 'done',
          message: 'Na fila de aprovações — revise e clique Ok.',
        });
        return;
      }
      const result = await approveItem(Number(selectedSectorId), created.id);
      if (result.executionError) {
        patchAction(card.id, { status: 'error', message: result.executionError });
        return;
      }
      patchAction(card.id, {
        status: 'done',
        message: result.executionResult || 'Feito.',
      });
      if (result.navigatePath) {
        navigate(result.navigatePath);
        onClose();
      }
    } catch (e: unknown) {
      patchAction(card.id, {
        status: 'error',
        message: apiErrorMessage(e, 'Falha ao executar.'),
      });
    }
  };

  const startTransfer = useCallback(() => {
    if (!selectedSectorId) {
      setReply('Selecione uma conexão no menu para eu transferir.');
      setActions([]);
      return;
    }
    const active = readActiveContact();
    if (!active || active.sectorId !== Number(selectedSectorId)) {
      pendingHandoff.current = null;
      awaitingDestination.current = false;
      setActions([]);
      setReply('Abra a conversa do cliente antes de transferir. A transferência atribui a pessoa ou a conexão, notifica o destino e pausa a IA.');
      return;
    }
    pendingHandoff.current = active;
    awaitingDestination.current = true;
    setQuery('');
    setActions([]);
    setReply(`Vou transferir ${active.name}. Digite a pessoa ou a conexão de destino e pressione Enter.`);
  }, [selectedSectorId]);

  const essentialPrompts: CmdItem[] = useMemo(() => [
    {
      id: 'ess-decisions',
      title: 'O que precisa da minha decisão?',
      subtitle: 'Conversas e aprovações que dependem de você',
      keywords: ['decisao', 'precisa', 'hoje', 'atencao'],
      run: () => { void runNl('Mostre as conversas e itens que precisam da minha decisão agora.'); },
    },
    {
      id: 'ess-followup',
      title: 'Follow-up de propostas sem resposta',
      subtitle: 'Quem recebeu proposta e parou há mais de 3 dias',
      keywords: ['follow', 'proposta', 'sem resposta'],
      run: () => { void runNl('Faça follow-up com quem recebeu proposta e não respondeu há mais de três dias.'); },
    },
    {
      id: 'ess-schedule',
      title: 'Agendar reunião com a agenda disponível',
      subtitle: 'Consulta horários reais e prepara o agendamento',
      keywords: ['agendar', 'reuniao', 'agenda', 'calendar'],
      run: () => { void runNl('Agende uma reunião usando a agenda disponível. Peça o contato se faltar.'); },
    },
    {
      id: 'ess-transfer',
      title: 'Transferir cliente com contexto',
      subtitle: 'Atribui, notifica e pausa a IA no destino que você indicar',
      keywords: ['transferir', 'handoff', 'financeiro'],
      run: () => startTransfer(),
    },
    {
      id: 'ess-proposal',
      title: 'Criar proposta com base no contexto',
      subtitle: 'Usa memória e conversa já registrada',
      keywords: ['proposta', 'orcamento', 'documento'],
      run: () => { void runNl('Crie uma proposta com base no que já conversamos. Peça o contato se faltar.'); },
    },
  ], [runNl, startTransfer]);

  const centralPrompts: CmdItem[] = useMemo(() => (centralCtx ? CENTRAL_SUGGESTED_QUESTIONS.map((question, i) => ({
    id: `ess-central-${i}`,
    title: question,
    subtitle: `Responde com os números da Central · ${centralCtx.label}`,
    keywords: ['central', 'analise', 'numeros'],
    run: () => { setQuery(question); void runNl(question); },
  })) : []), [centralCtx, runNl]);

  const items: CmdItem[] = useMemo(() => [
    ...centralPrompts,
    {
      id: 'mind',
      title: 'Abrir MENDS AI em tela cheia',
      subtitle: 'Chat expandido para análises longas',
      keywords: ['mends', 'ai', 'mente', 'chat'],
      run: () => goMind(),
    },
    {
      id: 'hoje',
      title: 'Abrir Hoje',
      subtitle: 'Computador de bordo — decisões e operação agora',
      keywords: ['hoje', 'today', 'priority', 'bordo'],
      run: () => { navigate('/hoje'); onClose(); },
    },
    {
      id: 'registrar',
      title: 'Abrir Registrar',
      subtitle: 'Contar o que aconteceu (≠ Barra da IA)',
      keywords: ['registrar', 'register', 'relato', 'aconteceu'],
      run: () => { navigate('/registrar'); onClose(); },
    },
    {
      id: 'central',
      title: 'Central de Inteligência',
      subtitle: 'Análises · Riscos · Oportunidades · Aprovações',
      keywords: ['intelligence', 'briefing', 'central', 'insights'],
      run: () => { navigate('/central-inteligencia'); onClose(); },
    },
    {
      id: 'approvals',
      title: 'Ir para Aprovações',
      subtitle: 'Fila de autonomia — Ok executa',
      keywords: ['approvals', 'aprovacoes', 'ok', 'queue'],
      run: () => { navigate('/central-inteligencia?tab=aprovacoes'); onClose(); },
    },
    {
      id: 'opportunities',
      title: 'Oportunidades',
      subtitle: 'Radar de receita do funil',
      keywords: ['opportunities', 'pipeline', 'oportunidades'],
      run: () => { navigate('/central-inteligencia?tab=oportunidades'); onClose(); },
    },
    {
      id: 'conversations',
      title: 'Ir para Conversas',
      subtitle: 'Inbox do WhatsApp',
      keywords: ['conversations', 'chat', 'inbox', 'whatsapp'],
      run: () => { navigate('/chat'); onClose(); },
    },
    ...essentialPrompts,
  ], [goMind, navigate, onClose, essentialPrompts, centralPrompts]);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return items;
    return items.filter((it) => {
      const blob = normalize(`${it.title} ${it.subtitle} ${it.keywords.join(' ')}`);
      return blob.includes(q) || q.split(/\s+/).every((w) => w.length < 2 || blob.includes(w));
    });
  }, [items, query]);

  const nlOption: CmdItem | null = useMemo(() => {
    const q = query.trim();
    if (q.length < 2) return null;
    if (filtered.some((f) => normalize(f.title).includes(normalize(q)))) return null;
    return {
      id: 'nl',
      title: `Perguntar à MENDS: “${q.slice(0, 64)}${q.length > 64 ? '…' : ''}”`,
      subtitle: 'Consulta e executa aqui — ações sensíveis pedem confirmação',
      keywords: [],
      run: () => { void runNl(q); },
    };
  }, [filtered, query, runNl]);

  const list = useMemo(() => (nlOption ? [nlOption, ...filtered] : filtered), [filtered, nlOption]);
  const showResult = busy || reply != null;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    resetInline();
    const t = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && awaitingDestination.current) {
        e.preventDefault();
        if (!busy && query.trim()) void runNl(query.trim());
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        if (confirm) setConfirm(null);
        else if (showResult && !busy) {
          resetInline();
        } else onClose();
      } else if (showResult) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!busy && query.trim()) void runNl(query.trim());
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, Math.max(list.length - 1, 0)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (busy) return;
        const item = list[active];
        if (item) item.run();
        else if (query.trim()) void runNl(query.trim());
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, list, active, query, runNl, busy, confirm, showResult]);

  if (!open || !isMendsVisionEnabled()) return null;

  const iconFor = (id: string) => {
    if (id === 'mind' || id === 'nl') return <FiCpu size={16} />;
    if (id === 'central' || id.startsWith('ess-')) return <FiSun size={16} />;
    if (id === 'approvals') return <FiCheckSquare size={16} />;
    if (id === 'conversations') return <FiMessageSquare size={16} />;
    if (id === 'opportunities') return <FiTarget size={16} />;
    return <FiArrowRight size={16} />;
  };

  const onQueryChange = (value: string) => {
    setQuery(value);
    if (awaitingDestination.current) return;
    if (reply != null && !busy) {
      setReply(null);
      setActions([]);
    }
  };

  return (
    <div className="mai-cmdk-backdrop" onClick={onClose} role="presentation">
      <div
        className={`mai-cmdk ${showResult ? 'is-executing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Barra da IA"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mai-cmdk-search">
          <FiSearch size={18} className="mai-cmdk-search-icon" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Pergunte ou comande a MENDS…"
            autoComplete="off"
            spellCheck={false}
          />
          <span className="mai-cmd-kbd">esc</span>
        </div>
        {centralCtx && (
          <div className="mai-cmdk-scope" aria-label="Contexto da pergunta">
            <span>Central de Inteligência</span>
            <span>{centralCtx.label}</span>
            <button type="button" className="mai-cmdk-scope-clear" onClick={() => setCentralCtx(null)}>
              Usar a conexão do menu
            </button>
          </div>
        )}

        {!showResult && (
          <div className="mai-cmdk-list">
            {list.length === 0 && (
              <div className="mai-cmdk-empty">Sem atalhos — Enter para perguntar à MENDS</div>
            )}
            {list.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                className={`mai-cmdk-item ${idx === active ? 'mai-cmdk-item-active' : ''}`}
                onMouseEnter={() => setActive(idx)}
                onClick={() => item.run()}
              >
                <span className="mai-cmdk-item-icon">{iconFor(item.id)}</span>
                <span className="mai-cmdk-item-text">
                  <span className="mai-cmdk-item-title">{item.title}</span>
                  <span className="mai-cmdk-item-sub">{item.subtitle}</span>
                </span>
                <FiArrowRight size={14} className="mai-cmdk-item-arrow" />
              </button>
            ))}
          </div>
        )}

        {busy && (
          <div className="mai-cmdk-loading">
            <FiLoader size={16} style={{ marginRight: 8 }} /> Analisando e preparando ações…
          </div>
        )}

        {!busy && reply != null && (
          <div className="mai-cmdk-result">
            {central ? <CentralAnswerBlock answer={central} /> : <p className="mai-cmdk-reply">{reply}</p>}
            {actions.length > 0 && (
              <div className="mai-cmdk-actions">
                {actions.map((a) => (
                  <div key={a.id} className="mai-cmdk-action">
                    <div>
                      <p className="mai-cmdk-action-title">{a.title}</p>
                      <p className="mai-cmdk-action-sub">
                        {a.message || a.subtitle || (needsConfirm(a) ? 'Requer confirmação' : 'Pronto para executar')}
                      </p>
                    </div>
                    {a.status !== 'done' && (
                      <button
                        type="button"
                        className="mai-btn"
                        disabled={a.status === 'busy'}
                        onClick={() => void executeAction(a)}
                      >
                        {needsConfirm(a) ? 'Enviar à fila' : a.kind === 'nav' || a.kind === 'contact' ? 'Abrir' : 'Ok'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="mai-btn-row" style={{ marginTop: 12 }}>
              <button type="button" className="mai-btn mai-btn-ghost" onClick={resetInline}>
                Nova pergunta
              </button>
            </div>
          </div>
        )}

        <div className="mai-cmdk-footer">
          <span><kbd>↑↓</kbd> Navegar</span>
          <span><kbd>↵</kbd> {showResult ? 'Perguntar de novo' : 'Executar'}</span>
          <span><kbd>esc</kbd> {showResult ? 'Voltar' : 'Fechar'}</span>
          <button
            type="button"
            className="mai-cmdk-footer-link"
            onClick={() => goMind(query.trim() || undefined)}
          >
            <FiExternalLink size={12} style={{ marginRight: 4 }} />
            Tela cheia
          </button>
        </div>
      </div>

      <MaiConfirmDialog
        open={!!confirm}
        title={confirm?.approvalTitle || confirm?.title || 'Confirmar ação sensível'}
        body={confirm?.approvalBody || confirm?.subtitle || 'Esta ação pode ser irreversível.'}
        confirmLabel="Confirmar e enfileirar"
        busy={confirmBusy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          setConfirmBusy(true);
          void executeAction(confirm, true).finally(() => {
            setConfirmBusy(false);
            setConfirm(null);
          });
        }}
      />
    </div>
  );
};

/** Global ⌘K / Ctrl+K opens the AI bar. */
export function useCommandPaletteHotkey(onOpen: () => void) {
  useEffect(() => {
    if (!isMendsVisionEnabled()) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpen]);
}

/**
 * Resposta analítica da Central: o texto já vem com os números trocados pelo servidor (cada um verificado contra os
 * dados da Central); aqui mostramos de onde veio cada número, os limites e o que faltaria para responder.
 */
export const CentralAnswerBlock: React.FC<{ answer: CentralQuestionAnswer }> = ({ answer }) => {
  const status = centralStatusLabel(answer.status);
  const scope = [answer.periodLabel, answer.sectors.map((x) => x.name).join(', ')].filter(Boolean).join(' · ');
  return (
    <div className={`mai-cmdk-central is-${answer.status}`}>
      <div className="mai-cmdk-central-head">
        {status && <span className="mai-cmdk-central-status">{status}</span>}
        {scope && <span className="mai-cmdk-central-scope">{scope}</span>}
      </div>
      <div className="mai-cmdk-reply"><Markdown text={answer.answer} /></div>
      {answer.missing.length > 0 && (
        <div className="mai-cmdk-central-block">
          <p className="mai-cmdk-central-label">O que falta para responder</p>
          <ul>{answer.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {answer.limits.length > 0 && (
        <div className="mai-cmdk-central-block">
          <p className="mai-cmdk-central-label">Limites</p>
          <ul>{answer.limits.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {answer.citations.length > 0 && (
        <details className="mai-cmdk-central-block">
          <summary className="mai-cmdk-central-label">De onde vem cada número ({answer.citations.length})</summary>
          <ul>
            {answer.citations.map((c) => (
              <li key={c.key}><strong>{c.display}</strong> — {c.label}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};
